import { useState, useEffect, useMemo } from 'react'
import { Button } from '@/components/ui/Button'
import { UserPlus, Loader2 } from 'lucide-react'
import api from '@/services/api'
import { toast } from 'sonner'
import { UserModal } from '@/components/Configuracao/UserModal'
import { ResetPasswordModal } from '@/components/Configuracao/ResetPasswordModal'
import { UsersTabRow } from '@/components/Configuracao/UsersTabRow'
import { UsersTabPendingBanner } from '@/components/Configuracao/UsersTabPendingBanner'
import {
  canManageContractFinanceFlag,
  canManageUserLifecycle,
  isManagerRole,
  normalizeUser,
  type UserItem,
} from '@/components/Configuracao/usersTabConstants'
import { useAuthStore } from '@/stores/authStore'

const BASE_TABLE_HEADERS = [
  'Nome',
  'E-mail',
  'Cargo',
  'Setor',
  'Senha',
  'E-mails fin.',
  'Status',
  'Ações',
] as const

export function UsersTab() {
  const viewerRole = useAuthStore((s) => s.user?.role)
  const canManageFinanceFlag = canManageContractFinanceFlag(viewerRole)
  const canLifecycle = canManageUserLifecycle(viewerRole)
  const tableHeaders = useMemo(
    () =>
      canManageFinanceFlag
        ? ([
            'Nome',
            'E-mail',
            'Cargo',
            'Setor',
            'Senha',
            'E-mails fin.',
            'Valores fin.',
            'Status',
            'Ações',
          ] as const)
        : BASE_TABLE_HEADERS,
    [canManageFinanceFlag],
  )
  const colSpan = tableHeaders.length

  const [users, setUsers] = useState<UserItem[]>([])
  const [loading, setLoading] = useState(true)
  const [modalUser, setModalUser] = useState<UserItem | null | undefined>(undefined)
  const [resetUserId, setResetUserId] = useState<string | null>(null)
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(null)
  const [financeTogglingId, setFinanceTogglingId] = useState<string | null>(null)

  const fetchUsers = () => {
    setLoading(true)
    api
      .get('/users')
      .then((resp) => {
        const rawUsers = resp.data.data ?? resp.data ?? []
        setUsers(rawUsers.map((u: Record<string, unknown>) => normalizeUser(u)))
      })
      .catch(() => {
        setUsers([])
        toast.error('Erro ao carregar usuários. Verifique se a API está atualizada.')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const toggleActive = async (id: string) => {
    if (!canLifecycle) {
      toast.error('Somente administradores podem ativar ou desativar usuários')
      return
    }
    try {
      await api.patch(`/users/${id}/toggle-active`)
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, active: !u.active } : u)))
    } catch {
      toast.error('Erro ao alterar status do usuário')
    }
  }

  const toggleContractFinance = async (user: UserItem) => {
    if (!canManageFinanceFlag || !isManagerRole(user.role)) return
    setFinanceTogglingId(user.id)
    const next = !user.canViewContractFinance
    try {
      await api.patch(`/users/${user.id}`, { canViewContractFinance: next })
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, canViewContractFinance: next } : u)),
      )
      toast.success(next ? 'Valores financeiros liberados' : 'Valores financeiros removidos')
    } catch {
      toast.error('Erro ao alterar acesso a valores financeiros')
    } finally {
      setFinanceTogglingId(null)
    }
  }

  const resendSetupEmail = async (user: UserItem) => {
    if (!canLifecycle) {
      toast.error('Somente administradores podem gerenciar senha de usuários')
      return
    }
    if (
      !window.confirm(
        `Reenviar e-mail de cadastro para ${user.name} (${user.email})?\n\nO link anterior será invalidado e um novo será gerado (válido por 72h).`,
      )
    ) {
      return
    }

    setResendingInviteId(user.id)
    try {
      const res = await api.post(`/users/${user.id}/send-password-setup`)
      const sent = Boolean(res.data?.data?.emailSent)
      toast.success(
        sent
          ? `E-mail de cadastro reenviado para ${user.email}`
          : 'Token gerado, mas o e-mail não pôde ser enviado. Verifique o template em Configuração → E-mails.',
      )
      fetchUsers()
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: { message?: string } | string } } }
      const apiErr = axiosErr.response?.data?.error
      const message =
        typeof apiErr === 'string'
          ? apiErr
          : apiErr?.message || 'Erro ao reenviar e-mail de cadastro'
      toast.error(message)
    } finally {
      setResendingInviteId(null)
    }
  }

  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) => {
        const aPending = a.passwordSetupStatus !== 'complete' ? 0 : 1
        const bPending = b.passwordSetupStatus !== 'complete' ? 0 : 1
        return aPending - bPending || a.name.localeCompare(b.name, 'pt-BR')
      }),
    [users],
  )

  return (
    <div className='glass-card p-0 overflow-hidden'>
      <div className='flex items-center justify-between px-4 py-3 border-b border-[var(--glass-border)]'>
        <h3
          className='font-semibold text-[var(--font-color-h3)]'
          style={{ fontSize: 'var(--font-size-h3)' }}
        >
          Usuários
        </h3>
        {canLifecycle && (
          <Button variant='primary' size='sm' icon={<UserPlus size={13} />} onClick={() => setModalUser(null)}>
            Adicionar Usuário
          </Button>
        )}
      </div>

      <UsersTabPendingBanner users={users} />

      <div className='overflow-x-auto'>
        <table className='w-full'>
          <thead>
            <tr className='border-b border-[var(--glass-border)]'>
              {tableHeaders.map((h) => (
                <th
                  key={h}
                  className='text-left px-4 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider whitespace-nowrap'
                  style={{ fontSize: 'var(--font-size-label)' }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colSpan} className='px-4 py-8 text-center text-text-muted text-sm'>
                  <Loader2 size={18} className='inline animate-spin mr-2' />
                  Carregando usuários...
                </td>
              </tr>
            ) : sortedUsers.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className='px-4 py-8 text-center text-text-muted text-sm'>
                  Nenhum usuário encontrado
                </td>
              </tr>
            ) : (
              sortedUsers.map((user) => (
                <UsersTabRow
                  key={user.id}
                  user={user}
                  showContractFinanceToggle={canManageFinanceFlag}
                  canManageLifecycle={canLifecycle}
                  resendingInviteId={resendingInviteId}
                  financeTogglingId={financeTogglingId}
                  onEdit={setModalUser}
                  onResetPassword={setResetUserId}
                  onResendSetup={resendSetupEmail}
                  onToggleActive={toggleActive}
                  onToggleContractFinance={toggleContractFinance}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {canLifecycle && modalUser !== undefined && (
        <UserModal
          user={modalUser}
          onClose={() => setModalUser(undefined)}
          onSaved={() => {
            setModalUser(undefined)
            fetchUsers()
          }}
        />
      )}
      {canLifecycle && resetUserId && (
        <ResetPasswordModal
          userId={resetUserId}
          onClose={() => setResetUserId(null)}
          onDone={() => {
            setResetUserId(null)
            fetchUsers()
          }}
        />
      )}
    </div>
  )
}
